import React, { useState, useEffect } from 'react';
import './PricingPage.css';
import { Check, ArrowRight, Apple, Monitor } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Helmet } from 'react-helmet';

const PricingPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [SKU_ID, setSKU_ID] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [stripePromise, setStripePromise] = useState(null);
  const [isStripeReady, setIsStripeReady] = useState(false);
  const navigate = useNavigate();

  // Fetch Stripe publishable key on component mount
  useEffect(() => {
    const fetchStripeKey = async () => {
      try {
        const response = await fetch('/config/stripe');
        if (!response.ok) throw new Error('Failed to fetch Stripe publishable key');
        const { publishableKey } = await response.json();
        if (!publishableKey) throw new Error('Publishable key is null or undefined');

        const stripe = await loadStripe(publishableKey);
        setStripePromise(stripe);
        setIsStripeReady(true);
      } catch (error) {
        console.error('Error fetching Stripe publishable key:', error);
      }
    };
    fetchStripeKey();
  }, []);

  // Utility function to verify if the user is logged in by calling a protected endpoint.
  // If not authenticated, redirect to sign-in.
  const checkAuthenticated = async () => {
    try {
      const resp = await fetch('/api/profile', {
        method: 'GET',
        credentials: 'include', // Send cookies
      });
      return resp.ok; // true if 200, false if not
    } catch (err) {
      console.error('Error checking authentication:', err);
      return false;
    }
  };

  // Updated function to check for an active subscription by passing the skuId as a query parameter.
  const checkActiveSubscription = async (skuId) => {
    try {
      const response = await fetch(`/api/check-active-subscription?skuId=${skuId}`, {
        method: 'GET',
        credentials: 'include', // Send cookies
      });
      const data = await response.json();
      // If the response is not OK or indicates an active subscription, show error.
      if (!response.ok || data.active) {
        setErrorMessage(data.message || 'You already have an active subscription.');
        setIsErrorModalOpen(true);
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error checking active subscription:', error);
      setErrorMessage('An error occurred while checking your subscription.');
      setIsErrorModalOpen(true);
      return false;
    }
  };

  // Open the OS selection modal if authenticated.
  const handleOpenModal = async (plan) => {
    const authenticated = await checkAuthenticated();
    if (!authenticated) {
      navigate('/signin', { state: { target: '/pricing', plan } });
      return;
    }
    setSelectedPlan(plan);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleCloseErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  // When the user clicks on an OS option:
  const handleOSClick = async (os) => {
    let skuId = '';
    if (selectedPlan === 'Free Trial') {
      skuId = os === 'Windows' ? 'Free_Win01' : 'Free_Mac01';
    } else if (selectedPlan === 'Standard') {
      skuId = os === 'Windows' ? 'STD_Win01' : 'STD_Mac01';
    }

    setSKU_ID(skuId);
    setIsModalOpen(false);

    // First, check if the user already has an active subscription for the selected SKU group.
    const canProceed = await checkActiveSubscription(skuId);
    if (!canProceed) return;

    if (selectedPlan === 'Free Trial') {
      // ✏️ NEW: register the free‐trial subscription so backend can send the code
      try {
        const resp = await fetch('/api/update-subscription', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ skuId })
        });
        if (!resp.ok) throw new Error('Failed to register free trial');
      } catch (err) {
        console.error(err);
        setErrorMessage('Could not register your free trial. Please try again.');
        setIsErrorModalOpen(true);
        return;
      }

      // show the "success" dialog
      setIsSuccessModalOpen(true);

    } else {
      // Standard → go to Stripe Checkout
      await redirectToCheckout(skuId);
    }
  };

  // Redirect to Stripe Checkout.
  const redirectToCheckout = async (skuId) => {
    try {
      if (!stripePromise) {
        console.error('Stripe is not initialized yet.');
        return;
      }
      const stripe = await stripePromise;
      if (!stripe) {
        console.error('Stripe instance is null. Initialization failed.');
        return;
      }
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Send cookies
        body: JSON.stringify({ skuId }),
      });
      if (!response.ok) {
        const { message } = await response.json();
        console.error('Error creating checkout session:', message);
        return;
      }
      const session = await response.json();
      await stripe.redirectToCheckout({ sessionId: session.id });
    } catch (error) {
      console.error('Error redirecting to checkout:', error);
    }
  };

  const plans = [
    {
      name: 'Free Trial',
      price: '$0',
      period: '/month',
      description: 'Perfect for getting started',
      features: [
        'First 2 months free',
        '$5.99 after trial expires',
        'Best for individual users',
        'Email support',
        'Limited AI insights'
      ],
      buttonText: 'Start Free Trial',
      popular: false
    },
    {
      name: 'Standard',
      price: '$54',
      period: '/year',
      description: 'Most popular choice',
      features: [
        'First 3 months free',
        'Best for individual users',
        'Email support',
        'AI-driven neurofeedback',
        'Advanced analytics'
      ],
      buttonText: 'Get Started',
      popular: true
    },
    {
      name: 'Enterprise',
      price: 'Contact Us',
      period: '',
      description: 'For organizations',
      features: [
        'Employer dashboards',
        'Best for organizations',
        'Priority support',
        'Advanced AI insights',
        'Manage team productivity and workload'
      ],
      buttonText: 'Contact Sales',
      popular: false
    }
  ];

  return (
    <div className="pricing-page-container">
      <Helmet>
        <title>Pricing Plans - WhiteMatter</title>
        <meta
          name="description"
          content="Explore our pricing plans at WhiteMatter. Choose the right plan for you—Free Trial, Standard, or Enterprise—to optimize productivity and maintain mental well-being."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="pricing-hero">
        <div className="pricing-hero-content">
          <h1 className="pricing-hero-title">
            WhiteMatter <span className="pricing-hero-accent">Pricing Plans</span>
          </h1>
          <p className="pricing-hero-subtitle">
            Get started with WhiteMatter and transform your productivity
          </p>
        </div>
      </section>

      {/* Pricing Cards Section */}
      <section className="pricing-cards-section">
        <div className="pricing-section-wrapper">
          <div className="pricing-cards-grid">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`pricing-card ${plan.popular ? 'pricing-card-popular' : ''}`}
              >
                {plan.popular && (
                  <div className="pricing-popular-badge">
                    Most Popular
                  </div>
                )}
                
                <div className="pricing-card-header">
                  <h3 className="pricing-card-title">{plan.name}</h3>
                  <p className="pricing-card-description">{plan.description}</p>
                  <div className="pricing-card-price">
                    <span className="pricing-price-amount">{plan.price}</span>
                    {plan.period && <span className="pricing-price-period">{plan.period}</span>}
                  </div>
                </div>

                <div className="pricing-card-features">
                  <ul className="pricing-features-list">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="pricing-feature-item">
                        <Check className="pricing-feature-icon" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pricing-card-footer">
                  <button
                    className={`pricing-card-button ${plan.popular ? 'pricing-button-primary' : 'pricing-button-secondary'}`}
                    onClick={() => {
                      if (plan.name === 'Enterprise') {
                        navigate('/contact');
                      } else {
                        handleOpenModal(plan.name);
                      }
                    }}
                  >
                    {plan.buttonText}
                    <ArrowRight className="pricing-button-icon" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OS Selection Modal */}
      {isModalOpen && (
        <div className="pricing-modal-overlay">
          <div className="pricing-modal">
            <div className="pricing-modal-content">
              <h3 className="pricing-modal-title">
                Choose your OS for the {selectedPlan} plan
              </h3>
              <div className="pricing-os-grid">
                <button
                  className="pricing-os-option"
                  onClick={() => handleOSClick('MacOS')}
                >
                  <Apple className="pricing-os-icon" />
                  <span className="pricing-os-label">macOS</span>
                </button>
                <button
                  className="pricing-os-option"
                  onClick={() => handleOSClick('Windows')}
                >
                  <Monitor className="pricing-os-icon" />
                  <span className="pricing-os-label">Windows</span>
                </button>
              </div>
              <button className="pricing-modal-close" onClick={handleCloseModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {isSuccessModalOpen && (
        <div className="pricing-modal-overlay">
          <div className="pricing-modal">
            <div className="pricing-modal-content pricing-success-modal">
              <div className="pricing-success-icon">
                <Check />
              </div>
              <h3 className="pricing-modal-title">Success!</h3>
              <p className="pricing-modal-message">
                Check your email for a registration code.
              </p>
              <button
                className="pricing-modal-button"
                onClick={() => setIsSuccessModalOpen(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {isErrorModalOpen && (
        <div className="pricing-modal-overlay">
          <div className="pricing-modal">
            <div className="pricing-modal-content pricing-error-modal">
              <h3 className="pricing-modal-title">Error</h3>
              <p className="pricing-modal-message">{errorMessage}</p>
              <button
                className="pricing-modal-button"
                onClick={handleCloseErrorModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingPage;